import React from 'react';

import debounce from 'lodash/debounce';
import { useForm, useFormState } from 'react-final-form';

import { Flex } from '@qality/common-components/src/atoms/Flex';
import { Text } from '@qality/common-components/src/atoms/Text';
import {
  PaginationProps,
  TestCyclesContext,
} from '@qality/common-components/src/lib/TestCyclesProvider';
import { SpinnerLoader } from '@qality/common-components/src/molecules/SpinnerLoader';
import DeleteTestCycleDialog from '@qality/common-components/src/test-cycle/DeleteTestCycleDialog';
import { TestCycle } from '@qality/common-model';
import { TestCycleHelper } from '@qality/common-service';

import { TestCyclesOverviewTableView } from './TestCyclesOverviewTable.view';

export interface TestCyclesOverviewTableProps {
  refreshCount: number;
  page?: number;
  searchText?: string;
  projectId?: string;
  versionId?: string;
  state?: boolean | null;
  onLoadComplete: (pagination: PaginationProps | null) => void;
}

export const TestCyclesOverviewTable: React.FC<TestCyclesOverviewTableProps> =
  ({
    page,
    searchText,
    projectId,
    versionId,
    refreshCount,
    onLoadComplete,
    state,
  }: TestCyclesOverviewTableProps) => {
    const { change } = useForm();
    const { values } = useFormState();

    const [dialogTestCycle, setDialogTestCycle] =
      React.useState<TestCycle | null>(null);

    const { isLoading, error, testCycles, fetchTestCycles, pagination } =
      React.useContext(TestCyclesContext);

    const debouncedFetchTestCycles = React.useCallback(
      debounce(fetchTestCycles, 350),
      []
    );

    const openDialog = (testCycle: TestCycle) => setDialogTestCycle(testCycle);
    const closeDialog = () => {
      setDialogTestCycle(null);
    };
    const deleteCycle = async () => {
      await TestCycleHelper.deleteTestCycle(dialogTestCycle, false);

      // if last page was displayed and it was last element on a page
      if (
        values.pagination.page > 1 &&
        values.pagination.pages === values.pagination.page &&
        (values.pagination.total - 1) % values.pagination.size === 0
      ) {
        change('pagination.page', values.pagination.page - 1);
      }
      change('refreshCount', values.refreshCount + 1);
      closeDialog();
    };

    // reload TestCycles list when at least one parameter will be updated
    React.useEffect(() => {
      debouncedFetchTestCycles(
        projectId,
        searchText,
        page - 1,
        versionId,
        state
      );
    }, [page, projectId, versionId, refreshCount, state]);

    React.useEffect(() => {
      pagination?.page && onLoadComplete(pagination);
    }, [pagination]);

    const hasFilters = !!searchText || !!projectId;

    if (error) {
      return (
        <Flex column fillHeight fillArea justifyCenter alignCenter>
          <Text>{error.message}</Text>
          <Text>{error.response?.data?.message}</Text>
        </Flex>
      );
    }

    return (
      <>
        {isLoading ? (
          <Flex fillHeight fillArea justifyCenter alignCenter>
            <SpinnerLoader text='Loading..' size='large' />
          </Flex>
        ) : (
          <TestCyclesOverviewTableView
            hasFilters={hasFilters}
            testCycles={testCycles}
            onDelete={openDialog}
          />
        )}
        <DeleteTestCycleDialog
          isOpen={dialogTestCycle}
          onClose={closeDialog}
          onDelete={deleteCycle}
          testCycleName={dialogTestCycle?.name}
        />
      </>
    );
  };
